/* eslint-disable prettier/prettier */
import React, { FC, useEffect, useState } from 'react';
import { Row, Col, Spin, Alert } from 'antd';
import { useQuery } from '@apollo/client';
import operations from 'apollo/operations';
import {
  Insurance as InsuranceType,
  InsuranceSimulationProduct,
  CoverageType,
  Customer,
} from 'apollo/types';
import { NextButton } from 'components/ui';
import { InsuranceCard } from 'components/insurance';
import hdi from 'assets/images/hdi.png';
import chilena from 'assets/images/zurich-logo-blue.svg';
import bci from 'assets/images/bci.png';
import { findIndex } from 'lodash';
import Header from 'components/ui/Header';
import InsuranceProposalFormModal from 'components/insurance/insuranceProposalFormModal';
import { InfoCircleOutlined } from '@ant-design/icons';
import InsuranceSuccess from 'components/insurance/insuranceSuccess';

interface InsuranceProduct extends InsuranceSimulationProduct {
  selected: boolean;
  disabled: boolean;
  show: boolean;
  coverageDetails: string;
}

const InsuranceProposalForm: FC = (): JSX.Element => {
  const [insuranceProductsOption, setInsuranceProductsOption] = useState<
    InsuranceProduct[]
  >([]);

  const [modalVisible, setModalVisible] = useState(false);
  const [createdProposal, setCreatedProposal] = useState(false);
  const [selectedProduct, setSelectedProduct] =
    React.useState<InsuranceProduct | null>(null);

  const [showSuccess, setShowSuccess] = React.useState<boolean>(false);
  const [policyDocument, setPolicyDocument] = React.useState<string>('');

  const { data: insuranceData, loading: getInsuranceLoading } = useQuery<{
    getInsurance: InsuranceType;
  }>(operations.GET_INSURANCE, {
    fetchPolicy: 'network-only',
  });

  const { data: customerData } = useQuery<{
    customer: Customer;
  }>(operations.GET_CUSTOMER_INFO, {
    fetchPolicy: 'network-only',
  });

  const { data: policyData } = useQuery<{
    getPolicy: string;
  }>(operations.GET_POLICY, {
    fetchPolicy: 'no-cache',
  });

  const baseInsuranceProduct = (simulation: InsuranceSimulationProduct) => {
    return {
      ...simulation,
      name: `Deducible ${simulation.deductible}UF`,
      selected: false,
      disabled: false,
      show: true,
    };
  };

  const sortInsuranceProducts = (a: InsuranceProduct, b: InsuranceProduct) => {
    const coverageOrder = [
      CoverageType.Light,
      CoverageType.Estandar,
      CoverageType.Full,
      CoverageType.Premium,
    ];

    const coverageA = a.coverageType as CoverageType;
    const coverageB = b.coverageType as CoverageType;

    const indexA = coverageOrder.indexOf(coverageA);
    const indexB = coverageOrder.indexOf(coverageB);

    return indexA - indexB;
  };

  const handleProductSelection = (productId: string) => {
    const index = findIndex(
      insuranceProductsOption,
      (option) => option.productId === productId,
    );

    const newInsuranceOptions = [
      ...insuranceProductsOption.map((option) => {
        return {
          ...option,
          selected: false,
        };
      }),
    ];
    newInsuranceOptions[index].selected =
      !insuranceProductsOption[index].selected;
    setInsuranceProductsOption(newInsuranceOptions);
    setSelectedProduct(newInsuranceOptions[index]);
  };

  const setInsuranceOptions = () => {
    if (
      insuranceData &&
      insuranceData.getInsurance?.selectedInsuranceProducts
    ) {
      const data = insuranceData.getInsurance.selectedInsuranceProducts?.map(
        (insurance) => baseInsuranceProduct(insurance),
      );
      if (data && data.length > 0) {
        let sortedData = data.sort(sortInsuranceProducts);

        if (insuranceData?.getInsurance?.insuranceProposal) {
          const product = sortedData.find(
            (option) =>
              option.productId ===
              insuranceData.getInsurance?.insuranceProposal?.proposalProductId,
          ) as InsuranceProduct;

          sortedData = sortedData.filter(
            (option) => option.productId === product.productId,
          );

          setSelectedProduct(product);
        }
        setInsuranceProductsOption(sortedData);
      }
    }

    if (insuranceData?.getInsurance?.insuranceProposal) {
      setCreatedProposal(true);
    }
  };

  useEffect(() => {
    setInsuranceOptions();
  }, [insuranceData]);

  useEffect(() => {
    if (policyData?.getPolicy) {
      setPolicyDocument(policyData.getPolicy);
      setShowSuccess(true);
    }
  }, [policyData]);

  const logos: Record<string, string> = {
    BCI: bci,
    CHILEAN: chilena,
    HDI: hdi,
  };

  const getAlertMessage = () => {
    if (selectedProduct?.insuranceProvider === 'CHILEAN') {
      return (
        <p style={{ fontSize: '12px' }}>
          La propuesta ha sido generado con éxito. Para comenzar con el proceso
          de emisión, se debe realizar la{' '}
          <a
            href="https://services.webchilena.cl/PaymentReports/Suscripcion"
            target="_blank"
            rel="noreferrer"
          >
            {' '}
            suscripción del pago indicando el número de propuesta.
          </a>{' '}
          Cuando la suscripción sea realizada con éxito, se enviará un correo
          con el certificado de coberturas. El PDF de la propuesta puede
          visualizarlo{' '}
          <a
            href={
              insuranceData?.getInsurance.insuranceProposal
                ?.proposalInternalWebPageUrl as string
            }
            target="_blank"
            rel="noreferrer"
          >
            {' '}
            aquí
          </a>
          . Numero de propuesta:{' '}
          {insuranceData?.getInsurance.insuranceProposal?.proposalInternalId}
        </p>
      );
    }
    if (selectedProduct?.insuranceProvider === 'BCI') {
      return (
        <p style={{ fontSize: '12px' }}>
          La propuesta ha sido generado con éxito. Se ha enviado un correo con
          el detalle para comenzar con el proceso del pago. El PDF de la
          propuesta puede visualizarlo{' '}
          <a
            href={
              insuranceData?.getInsurance.insuranceProposal
                ?.proposalInternalWebPageUrl as string
            }
            target="_blank"
            rel="noreferrer"
          >
            {' '}
            aquí
          </a>
          . Numero de propuesta:{' '}
          {insuranceData?.getInsurance.insuranceProposal?.proposalInternalId}
        </p>
      );
    }
    return (
      <p style={{ fontSize: '12px' }}>
        La propuesta ha sido generado con éxito. Se ha enviado un correo con el
        detalle para comenzar con el proceso de firma, en caso de no
        visualizarlo haga click{' '}
        <a
          href={
            insuranceData?.getInsurance.insuranceProposal
              ?.proposalInternalWebPageUrl as string
          }
          target="_blank"
          rel="noreferrer"
        >
          {' '}
          aquí
        </a>{' '}
        . Cuando la poliza haya sido emitida, se mostrará el detalle del
        documento. Numero de propuesta:{' '}
        {insuranceData?.getInsurance.insuranceProposal?.proposalInternalId}
      </p>
    );
  };

  return (
    <Spin spinning={getInsuranceLoading}>
      <div>
        <>
          <InsuranceProposalFormModal
            visible={modalVisible}
            handleOk={() => setModalVisible(false)}
            handleCancel={() => setModalVisible(false)}
            selectedProduct={selectedProduct as InsuranceProduct}
            insuranceData={insuranceData?.getInsurance}
            customerData={customerData?.customer}
          />
          <Header />
          <Row gutter={[12, 12]} style={{ marginTop: '24px' }}>
            {insuranceProductsOption
              .filter((option) => {
                return (
                  ((option.insuranceProvider === 'BCI' ||
                    option.insuranceProvider === 'CHILEAN') &&
                    insuranceData?.getInsurance?.insuranceInput.paymentType !==
                      'CASH') ||
                  option.insuranceProvider === 'HDI'
                );
              })
              .map((insuranceOption, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Col xs={24} md={6} key={index.toString()}>
                  <InsuranceCard
                    pdfUrl={insuranceOption.PdfUrl}
                    coverage={insuranceOption.coverageType as string}
                    period={insuranceOption.period}
                    deductible={insuranceOption.deductible}
                    monthlyValue={insuranceOption.monthlyValue}
                    name={insuranceOption.name || ''}
                    selected={
                      insuranceOption.selected ||
                      (createdProposal &&
                        insuranceData?.getInsurance?.insuranceProposal
                          ?.proposalProductId === insuranceOption.productId)
                    }
                    details={insuranceOption.coverageDetails}
                    paymentType={insuranceOption.paymentType}
                    disabled={
                      createdProposal &&
                      insuranceData?.getInsurance.insuranceProposal
                        ?.proposalProductId === insuranceOption.productId
                    }
                    monthlyValueInUF={insuranceOption.monthlyValueInUF}
                    installments={insuranceOption.installments}
                    insuranceType={insuranceOption.useType}
                    onClick={() =>
                      handleProductSelection(insuranceOption.productId)
                    }
                    insuranceCompany={{
                      name: insuranceOption.insuranceProvider,
                      logo: logos[insuranceOption.insuranceProvider],
                    }}
                  />
                </Col>
              ))}
          </Row>
          {createdProposal && (
            <Row style={{ marginTop: '24px' }}>
              <Alert
                showIcon
                style={{ borderRadius: '4px' }}
                icon={<InfoCircleOutlined />}
                message={getAlertMessage()}
                type="info"
              />
            </Row>
          )}
          {showSuccess && (
            <InsuranceSuccess
              document={policyDocument}
              selectedProduct={selectedProduct as InsuranceSimulationProduct}
            />
          )}
        </>
        {!createdProposal && !showSuccess && (
          <NextButton
            text="Generar propuesta"
            onClick={() => {
              setModalVisible(true);
            }}
            disabled={
              insuranceProductsOption.filter((option) => option.selected)
                .length === 0
            }
            loading={false}
          />
        )}
      </div>
    </Spin>
  );
};

export default InsuranceProposalForm;
